import React, { useState } from 'react';
import { RotateCcw, Plus, Printer } from 'lucide-react';
import { ReturnForm } from '../ReturnForm/ReturnForm';
import { printMovement } from '../print/printMovement';

export const ReturnModule = ({ assignments, materials, people = [], warehouses = [], onRegister }) => {
  const [showForm, setShowForm] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  const printReceipt = (r) => {
    printMovement(r);
  };

  const handleSave = async (returnData) => {
    // Manejo de lote de devoluciones (múltiples ítems)
    if (returnData.items && Array.isArray(returnData.items) && returnData.items.length > 0) {
      let okCount = 0;
      let failCount = 0;
      const failDetails = [];
      const successfulItems = [];
      const allObservations = [];

      for (const item of returnData.items) {
        try {
          const result = await onRegister(item);
          if (result && result.success) {
            okCount++;
            
            // Recuperar datos completos para el recibo
            const assign = assignments.find(a => String(a.id) === String(item.assignmentId));
            const material = materials.find(m => String(m.id) === String(assign?.materialId || item.materialId));
            
            // Incluir estado en la descripción o nombre
            const stateSuffix = item.materialState ? ` (${item.materialState})` : '';

            // Lógica de agrupación: Buscar si ya existe este material en la lista de exitosos
            const existingItemIndex = successfulItems.findIndex(si => 
              String(si.materialId) === String(material?.id) && 
              si.materialState === item.materialState &&
              String(si.serial || '').trim() === String(assign?.serial || item.serial || '').trim()
            );

            if (existingItemIndex !== -1) {
              // Si existe, sumar cantidad
              successfulItems[existingItemIndex].quantity = (Number(successfulItems[existingItemIndex].quantity) || 0) + (Number(item.returnQuantity) || 0);
              // Concatenar observaciones si son diferentes
              if (item.observations && !successfulItems[existingItemIndex].rawObservations.includes(item.observations)) {
                successfulItems[existingItemIndex].rawObservations.push(item.observations);
              }
            } else {
              // Si no existe, agregar nuevo
              successfulItems.push({
                materialId: material?.id,
                materialState: item.materialState,
                materialName: (assign?.materialName || item.materialName || '') + stateSuffix,
                materialCode: material?.code,
                materialUnit: material?.unit || 'Unidad',
                material_category: material?.category || material?.type || '',
                materialLocation: material?.location,
                materialStatus: material?.status,
                quantity: Number(item.returnQuantity) || 0,
                serial: String(assign?.serial || item.serial || '').trim(),
                brandName: String(item.brandName || assign?.brandName || material?.brand || '').trim(),
                rawObservations: item.observations ? [item.observations] : []
              });
            }

            if (item.observations) {
              allObservations.push(`${item.materialName}: ${item.observations}`);
            }
          } else {
            failCount++;
            failDetails.push(`${item.materialName}: ${result?.message || 'Error desconocido'}`);
          }
        } catch (error) {
          failCount++;
          failDetails.push(`${item.materialName}: ${error.message || 'Error de excepción'}`);
        }
      }

      if (okCount > 0) {
        const firstItem = returnData.items[0];
        const firstAssign = assignments.find(a => String(a.id) === String(firstItem.assignmentId));
        
        // Datos de cabecera tomados del primer ítem (asumiendo mismo responsable)
        const receipt = {
          type: 'Devolución',
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          responsibleName: firstItem.responsibleName || firstAssign?.responsibleName || '',
          responsibleDoc: firstItem.responsibleDoc || firstAssign?.responsibleDoc || '',
          
          // Campos de cabecera (usados si no hubiera tabla, pero printMovement usa tabla si hay items)
          materialName: 'Varios',
          quantity: successfulItems.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0),
          observations: allObservations.length > 0 ? allObservations.join('\n') : '',
          
          items: successfulItems,
        };
        
        setLastReceipt(receipt);
        printMovement(receipt);
        setShowForm(false);
        
        if (failCount > 0) {
          alert(`Proceso finalizado con observaciones.\nExitosos: ${okCount}\nFallidos: ${failCount}\n\nDetalles:\n${failDetails.join('\n')}`);
        } else {
          alert('Devoluciones registradas correctamente.');
        }
        return { success: true };
      } else {
        alert(`No se pudo registrar ninguna devolución.\n\nDetalles:\n${failDetails.join('\n')}`);
        return { success: false };
      }
    }

    // Flujo original para un solo ítem
    const result = await onRegister(returnData);
    if (result && result.success) {
      alert(result.message);
      const assign = assignments.find(a => String(a.id) === String(returnData.assignmentId));
      const material = materials.find(m => String(m.id) === String(assign?.materialId || returnData.materialId));
      const receipt = {
        type: 'Devolución',
        date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
        materialName: assign?.materialName || '',
        materialCode: material?.code,
        material_category: material?.category || material?.type || '',
        materialUnit: material?.unit,
        materialLocation: material?.location,
        materialStatus: material?.status,
        responsibleName: returnData.responsibleName || assign?.responsibleName || '',
        responsibleDoc: returnData.responsibleDoc || assign?.responsibleDoc || '',
        quantity: returnData.returnQuantity,
        serial: String(assign?.serial || returnData.serial || '').trim(),
        brandName: String(returnData.brandName || assign?.brandName || material?.brand || '').trim(),
        materialState: returnData.materialState,
        observations: returnData.observations
      };
      setLastReceipt(receipt);
      printMovement(receipt);
      setShowForm(false);
    } else {
      alert(result?.message || 'No fue posible registrar la devolución');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="section-title mb-4">Devolución de Materiales</h2>

      {lastReceipt && !showForm && (
        <div className="mb-4 p-3 border rounded bg-gray-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Movimiento registrado: {lastReceipt.type}</p>
            <p className="text-xs text-gray-600">{lastReceipt.materialName} ← {lastReceipt.responsibleName} ({lastReceipt.responsibleDoc || ''}) — Cant: {lastReceipt.quantity} — {lastReceipt.date}</p>
          </div>
          <button onClick={() => printReceipt(lastReceipt)} className="bg-gray-800 text-white px-3 py-2 rounded flex items-center gap-2">
            <Printer size={16} /> Imprimir comprobante
          </button>
        </div>
      )}

      {!showForm ? (
        <div className="text-center py-12">
          <RotateCcw size={64} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">Registre la devolución de materiales asignados</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-500 text-white px-6 py-3 rounded flex items-center gap-2 hover:bg-blue-600 mx-auto"
          >
            <Plus size={20} /> Registrar Devolución
          </button>
        </div>
      ) : (
        <ReturnForm
          assignments={assignments}
          materials={materials}
          people={people}
          warehouses={warehouses}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};
